import React, { useState } from "react";
import { useData } from "../../hooks/useData";
import Card from "../ui/Card";
import Button from "../ui/Button";
import { DocumentIcon } from "../ui/Icons";
import { securelyTriggerN8n } from "../../lib/n8n";
import { N8N_WEBHOOKS } from "../../constants";
import { logger } from "../../lib/logger";
import { getViewerTimezone } from "../../lib/timezone";

// =============================================================================
// CONFIDENTIAL TABS
// =============================================================================
// PURPOSE: Tabs visible only to exec/owner/admin/client roles showing client tasks, files, communications.
//
// REQUIREMENTS:
// - 3 tabs: tasks, files, comms
// - Tasks: show status, due date, title
// - Files: show name, upload date, download link
// - Communications: show date, subject, message, add note functionality
// - All data fetched from DataContext (ClientPortalContext)
// =============================================================================

const ConfidentialTabs: React.FC = () => {
  const { clientTasks, clientFiles, clientCommunications } = useData();
  const [activeTab, setActiveTab] = useState<"tasks" | "files" | "comms">("tasks");
  const [noteInputs, setNoteInputs] = useState<Record<string, string>>({});

  const handleAddNote = async (id: string, note: string) => {
    try {
      await securelyTriggerN8n(N8N_WEBHOOKS.ADMIN_WORKFLOW, {
        action: "add_note",
        type: "communication",
        id: id,
        note: note,
      });
      alert("Note sent to N8N successfully.");
    } catch (e) {
      logger.error(e);
      alert("Failed to add note.");
    }
  };

  return (
    <Card className="min-h-[400px] anim-lift">
      <div className="border-b border-black/[0.08] mb-4">
        <nav className="-mb-px flex space-x-8">
          {["tasks", "files", "comms"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as any)}
              className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm capitalize anim-tap ${
                activeTab === tab
                  ? "border-blue-500 text-blue-400"
                  : "border-transparent text-glass-muted hover:text-glass hover:border-black/[0.12]"
              }`}
            >
              Client {tab}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === "tasks" && (
        <div className="space-y-3">
          {clientTasks.length > 0 ? (
            clientTasks.map((t) => (
              <div
                key={t.id}
                className="flex justify-between items-center p-3 bg-black/[0.03] rounded border border-black/[0.08]"
              >
                <div>
                  <p className="font-semibold text-glass">{t.title}</p>
                  <p className="text-xs text-glass-muted">
                    Assignee: {t.profiles?.full_name || "Unassigned"} • Due: {t.due_date}
                  </p>
                </div>
                <span
                  className={`px-2 py-1 text-xs rounded-full ${t.status === "completed" ? "bg-ds-green-bg text-ds-green" : "bg-ds-amber-bg text-ds-amber"}`}
                >
                  {t.status}
                </span>
              </div>
            ))
          ) : (
            <p className="text-glass-muted italic text-sm p-4 text-center">No tasks found.</p>
          )}
        </div>
      )}

      {activeTab === "files" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {clientFiles.length > 0 ? (
            clientFiles.map((f) => (
              <div
                key={f.id}
                className="flex items-center p-4 liquid-glass border border-black/[0.08] rounded hover:shadow-sm cursor-pointer"
                onClick={() => window.open(f.s3_url, "_blank", "noopener,noreferrer")}
              >
                <DocumentIcon />
                <div className="ml-3 overflow-hidden">
                  <p className="text-sm font-medium text-glass truncate" title={f.file_name}>
                    {f.file_name}
                  </p>
                  <p className="text-xs text-glass-muted">
                    Shared: {new Date(f.shared_at).toLocaleDateString("en-US")} by{" "}
                    {f.profiles?.full_name || "Unknown"}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <p className="text-glass-muted italic text-sm p-4 text-center col-span-2">
              No files found.
            </p>
          )}
        </div>
      )}

      {activeTab === "comms" && (
        <div className="space-y-4">
          {clientCommunications.length > 0 ? (
            clientCommunications.map((c) => (
              <div key={c.id} className="bg-black/[0.03] p-4 rounded border border-black/[0.08]">
                <div className="flex justify-between mb-2">
                  <span className="font-bold text-sm text-glass flex items-center gap-2">
                    {c.communication_type === "Zoom" && "🎥"} {c.communication_type}
                  </span>
                  <span className="text-xs text-glass-muted">
                    {new Date(c.created_at).toLocaleString("en-US", {
                      timeZone: getViewerTimezone(),
                    })}
                  </span>
                </div>
                <p className="text-sm text-glass mb-3 whitespace-pre-wrap">{c.notes}</p>
                {c.link && (
                  <a
                    href={c.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-400 hover:underline block mb-3"
                  >
                    View Recording/Link
                  </a>
                )}
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Add a comment..."
                    className="flex-1 text-xs border-black/[0.1] rounded px-2 py-1"
                    value={noteInputs[c.id] || ""}
                    onChange={(e) => setNoteInputs((prev) => ({ ...prev, [c.id]: e.target.value }))}
                  />
                  <Button
                    className="py-1 px-3 text-xs h-auto anim-tap"
                    onClick={() => {
                      const note = noteInputs[c.id] || "";
                      if (note.trim()) {
                        handleAddNote(c.id, note);
                        setNoteInputs((prev) => ({ ...prev, [c.id]: "" }));
                      }
                    }}
                  >
                    Add
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <p className="text-glass-muted italic text-sm p-4 text-center">
              No communications found.
            </p>
          )}
        </div>
      )}
    </Card>
  );
};

export default ConfidentialTabs;
